import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  ComputerDesktopIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { Button, Input, Card, CardContent, CardHeader, CardTitle, Modal, Badge } from '../../components/ui';
import { softwareService } from '../../services/softwareService';
import { formatDate } from '../../utils/formatters';
import { SUCCESS_MESSAGES } from '../../constants';

export const SoftwarePage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSoftware, setSelectedSoftware] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    is_active: ''
  });

  // Form states
  const [createForm, setCreateForm] = useState({
    name: '',
    version: '',
    description: '',
    is_active: true
  });
  const [createFormErrors, setCreateFormErrors] = useState({});

  const [editForm, setEditForm] = useState({
    name: '',
    version: '',
    description: '',
    is_active: true
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  const queryClient = useQueryClient();

  // Fetch software
  const { data: software, isLoading, error } = useQuery({
    queryKey: ['software', searchTerm, filters],
    queryFn: () => softwareService.getSoftware({ search: searchTerm, ...filters }),
  });

  // Create software mutation
  const createSoftwareMutation = useMutation({
    mutationFn: softwareService.createSoftware,
    onSuccess: () => {
      queryClient.invalidateQueries(['software']);
      setIsCreateModalOpen(false);
      resetCreateForm();
      console.log(SUCCESS_MESSAGES.SOFTWARE_CREATED || 'Software creado exitosamente');
    },
    onError: (error) => {
      console.error('Error creating software:', error);
      if (error.response?.data?.errors) {
        setCreateFormErrors(error.response.data.errors);
      }
    },
  });

  // Update software mutation
  const updateSoftwareMutation = useMutation({
    mutationFn: ({ id, data }) => softwareService.updateSoftware(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['software']);
      setIsEditModalOpen(false);
      setSelectedSoftware(null);
      resetEditForm();
      console.log(SUCCESS_MESSAGES.SOFTWARE_UPDATED || 'Software actualizado exitosamente');
    },
    onError: (error) => {
      console.error('Error updating software:', error);
      if (error.response?.data?.errors) {
        setEditFormErrors(error.response.data.errors);
      }
    },
  });

  // Delete software mutation
  const deleteSoftwareMutation = useMutation({
    mutationFn: softwareService.deleteSoftware,
    onSuccess: () => {
      queryClient.invalidateQueries(['software']);
      console.log(SUCCESS_MESSAGES.SOFTWARE_DELETED || 'Software eliminado exitosamente');
    },
  });

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Form handlers
  const handleCreateFormChange = (field, value) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (createFormErrors[field]) {
      setCreateFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      name: '',
      version: '',
      description: '',
      is_active: true
    });
    setCreateFormErrors({});
  };

  const handleCreateSoftware = (e) => {
    e.preventDefault();
    setCreateFormErrors({});
    
    createSoftwareMutation.mutate(createForm);
  };

  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
    resetCreateForm();
  };

  const handleDeleteSoftware = (softwareId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este software?')) {
      deleteSoftwareMutation.mutate(softwareId);
    }
  };

  const handleViewSoftware = (software) => {
    setSelectedSoftware(software);
    setIsViewModalOpen(true);
  };

  const handleEditSoftware = (software) => {
    setSelectedSoftware(software);
    setEditForm({
      name: software.name,
      version: software.version,
      description: software.description || '',
      is_active: software.is_active
    });
    setEditFormErrors({});
    setIsEditModalOpen(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (editFormErrors[field]) {
      setEditFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const resetEditForm = () => {
    setEditForm({
      name: '',
      version: '',
      description: '',
      is_active: true
    });
    setEditFormErrors({});
  };

  const handleEditSoftwareSubmit = (e) => {
    e.preventDefault();
    setEditFormErrors({});
    
    updateSoftwareMutation.mutate({
      id: selectedSoftware.id,
      data: editForm
    });
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedSoftware(null);
    resetEditForm();
  };

  // Helper functions
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Hace un momento';
    if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} h`;
    if (diffInSeconds < 2592000) return `Hace ${Math.floor(diffInSeconds / 86400)} días`;
    if (diffInSeconds < 31536000) return `Hace ${Math.floor(diffInSeconds / 2592000)} meses`;
    return `Hace ${Math.floor(diffInSeconds / 31536000)} años`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Software</h1>
        </div>
        <div className="grid gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-200 animate-pulse rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Software</h1>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-red-600">Error al cargar el software</p>
              <p className="text-sm text-gray-500 mt-2">{error.message}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Software</h1>
          <p className="text-gray-600">Gestiona el catálogo de software</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Nuevo Software
        </Button>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Input
                placeholder="Buscar software..."
                value={searchTerm}
                onChange={handleSearch}
                icon={<MagnifyingGlassIcon className="h-4 w-4" />}
              />
            </div>
            <div>
              <select
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value="true">Activos</option>
                <option value="false">Inactivos</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Software Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {software && software.length > 0 ? (
          software.map((item) => (
            <Card key={item.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 h-12 w-12">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <ComputerDesktopIcon className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg font-semibold text-gray-900 truncate">
                        {item.name}
                      </CardTitle>
                      <p className="text-sm text-gray-500">
                        v{item.version}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewSoftware(item)}
                      className="text-gray-400 hover:text-blue-600"
                      title="Ver detalles"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditSoftware(item)}
                      className="text-gray-400 hover:text-green-600"
                      title="Editar software"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteSoftware(item.id)}
                      className="text-gray-400 hover:text-red-600"
                      title="Eliminar software"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Description */}
                  {item.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {item.description}
                    </p>
                  )}


                  {/* Active Licenses */}
                  <div className="flex items-center text-sm text-gray-600">
                    <TagIcon className="h-4 w-4 mr-1" />
                    <span>{item.active_licenses_count || 0} licencias activas</span>
                  </div>

                  {/* Status */}
                  <div className="flex justify-between items-center">
                    <Badge variant={item.is_active ? 'success' : 'secondary'}>
                      {item.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {getTimeAgo(item.created_at)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="col-span-full">
            <Card>
              <CardContent className="p-12">
                <div className="text-center">
                  <div className="mx-auto h-12 w-12 text-gray-400">
                    <ComputerDesktopIcon className="h-12 w-12" />
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No hay software</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm || Object.values(filters).some(f => f) 
                      ? 'Intenta ajustar los filtros de búsqueda'
                      : 'Comienza agregando tu primer software'
                    }
                  </p>
                  {!searchTerm && !Object.values(filters).some(f => f) && (
                    <div className="mt-6">
                      <Button onClick={() => setIsCreateModalOpen(true)}>
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Nuevo Software
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Create Software Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={handleCreateModalClose}
        title="Nuevo Software"
      >
        <form onSubmit={handleCreateSoftware} className="p-6">
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre *
              </label>
              <Input
                value={createForm.name}
                onChange={(e) => handleCreateFormChange('name', e.target.value)}
                placeholder="Nombre del software"
                error={createFormErrors.name}
                required
              />
            </div>

            {/* Version */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Versión *
              </label>
              <Input
                value={createForm.version}
                onChange={(e) => handleCreateFormChange('version', e.target.value)}
                placeholder="1.0.0"
                error={createFormErrors.version}
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción
              </label>
              <textarea
                value={createForm.description}
                onChange={(e) => handleCreateFormChange('description', e.target.value)}
                placeholder="Descripción del software"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
              />
            </div>


            {/* Active Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={createForm.is_active}
                onChange={(e) => handleCreateFormChange('is_active', e.target.value === 'true')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCreateModalClose}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={createSoftwareMutation.isPending}
            >
              {createSoftwareMutation.isPending ? 'Creando...' : 'Crear Software'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* View Software Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Detalles del Software"
      >
        <div className="p-6">
          {selectedSoftware && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Nombre</label>
                <p className="text-gray-900 font-semibold">{selectedSoftware.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Versión</label>
                <p className="text-gray-900">v{selectedSoftware.version}</p>
              </div>
              {selectedSoftware.description && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Descripción</label>
                  <p className="text-gray-900">{selectedSoftware.description}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-700">Estado</label>
                <Badge variant={selectedSoftware.is_active ? 'success' : 'secondary'}>
                  {selectedSoftware.is_active ? 'Activo' : 'Inactivo'}
                </Badge>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Licencias Activas</label>
                <p className="text-gray-900">{selectedSoftware.active_licenses_count || 0}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Fecha de Creación</label>
                <p className="text-gray-900">{formatDate(selectedSoftware.created_at)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Última Actualización</label>
                <p className="text-gray-900">{formatDate(selectedSoftware.updated_at)}</p>
              </div>
            </div>
          )}
          <div className="flex justify-end mt-6">
            <Button onClick={() => setIsViewModalOpen(false)}>
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Software Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        title="Editar Software"
      >
        <form onSubmit={handleEditSoftwareSubmit} className="p-6">
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre *
              </label>
              <Input
                value={editForm.name}
                onChange={(e) => handleEditFormChange('name', e.target.value)}
                placeholder="Nombre del software"
                error={editFormErrors.name}
                required
              />
            </div>

            {/* Version */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Versión *
              </label>
              <Input
                value={editForm.version}
                onChange={(e) => handleEditFormChange('version', e.target.value)}
                placeholder="1.0.0"
                error={editFormErrors.version}
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción
              </label>
              <textarea
                value={editForm.description}
                onChange={(e) => handleEditFormChange('description', e.target.value)}
                placeholder="Descripción del software"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
              />
            </div>

            {/* Active Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={editForm.is_active}
                onChange={(e) => handleEditFormChange('is_active', e.target.value === 'true')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={handleEditModalClose}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={updateSoftwareMutation.isPending}
            >
              {updateSoftwareMutation.isPending ? 'Actualizando...' : 'Actualizar Software'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
